import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Package, ScanLine, Play, CheckCircle2, Truck, Undo2, Camera,
  Search, Loader2, X, ChevronRight, RefreshCw, Zap,
} from 'lucide-react';

// D-6A-UX HOTFIX (2026-02-04): 검수완료 + 송장미등록 상태에서 사용자 안내를 명확히 하고
// ⑥ 송장 등록 화면으로 원클릭 이동하는 CTA 를 추가한다. lifecycle · _classify · backend
// · [출고 처리] semantics · CJ Agent LOCK 모듈은 일체 변경하지 않는다.
const INVOICE_STAGE_ROUTE = '/seller/dashboard?section=orders&stage=invoice';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';
import ShippingLabelModal from './ShippingLabelModal';
import PartialShipmentModal from './PartialShipmentModal';
// STEP OMS-PARTIAL-PICK-2A-FIX · [피킹시작] 은 반드시 선택 UI 를 거쳐 item_refs 를 실어 보낸다.
import SelectivePickModal from './simple/SelectivePickModal';
// BARCODE/WMS A3 · REUSE shared scanner (USB/BT + camera BarcodeDetector).
// scanUrl prop routes it to the seller server-side scan endpoint; existing
// operator sites pass no scanUrl and keep default behaviour byte-identical.
import WarehouseScanInput from '@/components/operator/WarehouseScanInput';

// Phase 2 Fulfillment (Barcode Picking / Inspection / Ship-Prep).
// Camera scan button is prepared but launches keyboard-driven scan modal
// (per Phase 2 spec: real camera scanner UX is next phase).

const fmt = (d) => { if (!d) return '-'; try { return format(new Date(d), 'yyyy.MM.dd HH:mm', { locale: ko }); } catch { return '-'; } };
const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

// STEP OMS-OUTBOUND-UNIFY-1 · 5 sub-tab: 출고준비 / 피킹중 / 검수완료 / 송장대기 / 출고완료.
//   backend fulfillment.status 는 4단계 (pending/picking/inspected/shipped) · 5번째
//   가상 tab `awaiting_tracking` 은 status='inspected' + shipment.tracking_number
//   존재하는 부분집합 (client-side 분할).  검수완료 = inspected + !tracking.
const STATUS_LABEL = { pending: '출고준비', picking: '피킹중', inspected: '검수완료',
                        awaiting_tracking: '송장대기', shipped: '출고완료' };
const STATUS_CLS = {
  pending:   'bg-gray-100 text-gray-700 border-gray-200 dark:bg-white/5 dark:border-white/10',
  picking:   'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300',
  inspected: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300',
  awaiting_tracking: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/30 dark:text-indigo-300',
  shipped:   'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300',
};
// backend 조회 시 사용할 실제 status (송장대기·검수완료는 둘 다 inspected 로 조회 후 client filter).
const _BACKEND_STATUS = { pending: 'pending', picking: 'picking',
                          inspected: 'inspected', awaiting_tracking: 'inspected',
                          shipped: 'shipped' };

export default function FulfillmentSection({ onOpenUnifiedDetail } = {}) {
  const [statusTab, setStatusTab] = useState('pending');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickCode, setQuickCode] = useState('');
  const [scanTarget, setScanTarget] = useState(null); // seller_order
  const [selected, setSelected] = useState({}); // sid -> bool
  const [labelIds, setLabelIds] = useState(null);
  const [partialTarget, setPartialTarget] = useState(null); // { seller_order_id, order_number }
  // STEP OMS-PARTIAL-PICK-2A-FIX · [피킹시작] → SelectivePickModal 로 item_refs 확정 후 pick.
  const [pickTarget, setPickTarget] = useState(null); // { seller_order_id, order_number }
  const quickRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // STEP OMS-OUTBOUND-UNIFY-1 · 5-tab 매핑 (송장대기·검수완료는 inspected 공유).
      const params = { status: _BACKEND_STATUS[statusTab] || statusTab };
      if (q) params.q = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [rL, rS] = await Promise.all([
        api.get('/seller/fulfillment', { params }),
        api.get('/seller/fulfillment/summary'),
      ]);
      let items = rL.data?.items || [];
      // 검수완료 vs 송장대기 client-side 분할 (backend 는 단일 inspected 반환).
      if (statusTab === 'awaiting_tracking') {
        items = items.filter((r) => !r.shipment?.tracking_number);
      } else if (statusTab === 'inspected') {
        items = items.filter((r) => !!r.shipment?.tracking_number);
      }
      setRows(items);
      setSummary(rS.data || null);
    } catch (e) { toast.error(e?.response?.data?.detail || '불러오기 실패'); }
    finally { setLoading(false); }
  }, [statusTab, q, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const doQuickLookup = async () => {
    const c = quickCode.trim();
    if (!c) return;
    try {
      const r = await api.get('/seller/fulfillment/lookup', { params: { code: c } });
      setScanTarget(r.data);
      setQuickCode('');
      quickRef.current?.focus();
    } catch (e) { toast.error(e?.response?.data?.detail || '주문을 찾지 못했습니다.'); }
  };

  // STEP OMS-PARTIAL-PICK-2A-FIX · 표의 [피킹시작] 은 무조건 SelectivePickModal 을 통해
  //   item_refs 를 확정한 뒤 pick 을 요청한다.  전체 items 가 pick 되던 기존 버그 봉인.
  const startPicking = (row) => {
    setPickTarget({ seller_order_id: row.seller_order_id, order_number: row.order_number });
  };

  const bulkPick = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) { toast.error('선택된 주문이 없습니다.'); return; }
    try {
      const r = await api.post('/seller/fulfillment/bulk-pick', { seller_order_ids: ids });
      toast.success(`${r.data.applied.length}건 피킹 시작 (스킵 ${r.data.skipped.length})`);
      setSelected({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  return (
    <div className="space-y-4" data-testid="seller-fulfillment-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>출고관리</h2>
          <p className="text-xs text-gray-500 mt-1">
            바코드/SKU 스캔 → 검수 → 배송중 처리. 실제 택배사 집하 API·WMS는 미연동 (MOCK).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => {
            const ids = Object.keys(selected).filter((k) => selected[k]);
            if (ids.length === 0) { toast.error('출력할 주문을 선택해주세요.'); return; }
            setLabelIds(ids);
          }} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-black text-white dark:bg-white dark:text-black text-xs font-black" data-testid="ff-print-selected">
            🖨 선택 송장 출력
          </button>
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold hover:bg-black/5 dark:hover:bg-white/5" data-testid="ff-refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''}/> 새로고침
          </button>
        </div>
      </div>

      {/* Quick scan */}
      <div className="rounded-2xl border-2 border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3 space-y-2" data-testid="ff-quick-scan">
        <div className="flex items-center gap-1 text-xs font-black text-[#FF3B30]"><Zap size={12}/> 빠른 스캔 모드</div>
        <div className="flex gap-2">
          <input ref={quickRef} value={quickCode} onChange={(e) => setQuickCode(e.target.value)} autoFocus
            onKeyDown={(e) => e.key === 'Enter' && doQuickLookup()}
            placeholder="주문번호 또는 송장번호 스캔·입력 후 Enter"
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-transparent font-mono"
            data-testid="ff-quick-input" />
          <button onClick={doQuickLookup} className="px-4 py-2 rounded-lg bg-[#FF3B30] text-white text-xs font-black" data-testid="ff-quick-submit">주문 열기</button>
        </div>
      </div>

      {/* Summary · STEP OMS-OUTBOUND-UNIFY-1 · 5 sub-tab count. */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {['pending', 'picking', 'inspected', 'awaiting_tracking', 'shipped'].map((k) => (
          <div key={k} className={`rounded-2xl border p-3 ${STATUS_CLS[k]}`} data-testid={`ff-sum-${k}`}>
            <p className="text-[10px] font-black uppercase tracking-wider">{STATUS_LABEL[k]}</p>
            <p className="text-2xl font-black mt-1">{summary?.counts?.[k] || 0}</p>
          </div>
        ))}
        <div className="rounded-2xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/5 p-3" data-testid="ff-sum-today">
          <p className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-300">오늘 출고완료</p>
          <p className="text-2xl font-black mt-1 text-emerald-700 dark:text-emerald-300">{summary?.today_shipped || 0}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 space-y-2">
        <div className="flex flex-wrap gap-1" data-testid="ff-status-tabs">
          {['pending', 'picking', 'inspected', 'awaiting_tracking', 'shipped'].map((k) => (
            <button key={k} onClick={() => setStatusTab(k)} data-testid={`ff-tab-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-black border ${statusTab === k ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}>
              {STATUS_LABEL[k]} · {summary?.counts?.[k] || 0}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="주문번호 · 구매자 · 상품명 · SKU · 송장번호"
              className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="ff-search" />
          </div>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="ff-date-from"/>
          <span className="text-xs text-gray-400">~</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="ff-date-to"/>
        </div>
      </div>

      {statusTab === 'pending' && Object.values(selected).some(Boolean) && (
        <div className="p-3 rounded-xl bg-black text-white dark:bg-white dark:text-black flex items-center justify-between text-xs" data-testid="ff-bulk-bar">
          <span>선택 {Object.values(selected).filter(Boolean).length}건</span>
          <button onClick={bulkPick} className="px-3 py-1.5 rounded-full bg-[#FF3B30] text-white font-black" data-testid="ff-bulk-pick">일괄 피킹 시작</button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-16 text-center text-sm text-gray-400"><Loader2 className="animate-spin inline mr-1" size={14}/> 불러오는 중…</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="ff-empty">
          <Package className="mx-auto text-gray-300" size={28} />
          <p className="mt-3 text-sm font-bold">해당 조건의 주문이 없습니다.</p>
        </div>
      ) : (
        <>
          {/* PC table */}
          <div className="hidden md:block overflow-x-auto rounded-2xl border border-black/10 dark:border-white/10" data-testid="ff-table">
            <table className="w-full text-xs">
              <thead className="bg-black/[0.03] dark:bg-white/5">
                <tr className="text-left">
                  <th className="px-2 py-2">{statusTab === 'pending' && <input type="checkbox" onChange={(e) => {
                    const v = e.target.checked;
                    const s = {}; rows.forEach((r) => { s[r.seller_order_id] = v; }); setSelected(s);
                  }} data-testid="ff-select-all" />}</th>
                  <th className="px-2 py-2 font-black">주문번호</th>
                  <th className="px-2 py-2 font-black">구매자</th>
                  <th className="px-2 py-2 font-black">상품 (SKU / 옵션 × 수량)</th>
                  <th className="px-2 py-2 font-black">송장</th>
                  <th className="px-2 py-2 font-black">진행률</th>
                  <th className="px-2 py-2 font-black">출고상태</th>
                  <th className="px-2 py-2 font-black">액션</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const f = r.fulfillment || {}; const prog = f.progress || [];
                  const scanned = prog.reduce((a, p) => a + (p.scanned_qty || 0), 0);
                  const required = prog.reduce((a, p) => a + (p.required_qty || 0), 0);
                  return (
                    <tr key={r.seller_order_id} className="border-t border-black/5 dark:border-white/10" data-testid={`ff-row-${r.seller_order_id}`}>
                      <td className="px-2 py-2">{statusTab === 'pending' && (
                        <input type="checkbox" checked={!!selected[r.seller_order_id]}
                          onChange={(e) => setSelected({ ...selected, [r.seller_order_id]: e.target.checked })}
                          data-testid={`ff-select-${r.seller_order_id}`} />
                      )}</td>
                      <td className="px-2 py-2 font-mono">{r.order_number}<div className="text-[10px] text-gray-500">{fmt(r.created_at)}</div></td>
                      <td className="px-2 py-2">{r.buyer_snapshot?.name || '-'}<div className="text-[10px] text-gray-500">{r.buyer_snapshot?.phone || ''}</div></td>
                      <td className="px-2 py-2">
                        {/* STEP OMS-OUTBOUND-UNIFY-2 · 첫 상품 대표 이미지 + 외 N개 */}
                        <div className="flex items-start gap-2">
                          {(() => {
                            const first = (r.items || [])[0];
                            const img = first?.image_snapshot || first?.image || first?.thumbnail || '';
                            return (
                              <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex items-center justify-center"
                                   data-testid={`ff-thumb-${r.seller_order_id}`}>
                                {img
                                  ? <img src={img} alt="" className="w-full h-full object-cover" loading="lazy"
                                         onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                  : <Package size={14} className="text-gray-300" />}
                              </div>
                            );
                          })()}
                          <div className="min-w-0 flex-1">
                            {(r.items || []).slice(0, 3).map((it, i) => (
                              <div key={i} className="text-[11px] truncate">
                                {it.name_snapshot} × {it.quantity} <span className="text-gray-500">({Object.entries(it.option || {}).map(([k, v]) => `${v}`).join('·') || '기본'})</span>
                              </div>
                            ))}
                            {r.items?.length > 3 && <div className="text-[10px] text-gray-500">외 {r.items.length - 3}건</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 font-mono text-[11px]">
                        {r.shipment?.tracking_number ? (
                          <>{r.shipment.courier}<div className="text-gray-500">{r.shipment.tracking_number}</div></>
                        ) : <span className="text-red-500 font-bold">송장 미등록</span>}
                      </td>
                      <td className="px-2 py-2 font-mono">{scanned}/{required}</td>
                      <td className="px-2 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-black ${STATUS_CLS[f.status || 'pending']}`} data-testid={`ff-status-${r.seller_order_id}`}>
                          {STATUS_LABEL[f.status || 'pending']}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          {onOpenUnifiedDetail && (
                            <button onClick={() => onOpenUnifiedDetail(r.seller_order_id)}
                                    className="px-2 py-1 rounded-full border border-black/10 dark:border-white/10 text-[10px] font-bold hover:border-[#FF3B30]/50"
                                    data-testid={`ff-open-detail-${r.seller_order_id}`}
                                    title="주문 상세 (상품별 상태 표시)">
                              상세
                            </button>
                          )}
                          <RowActions row={r} onOpenScan={() => setScanTarget(r)} onPick={() => startPicking(r)} onRefresh={load} onOpenPartial={() => setPartialTarget({ seller_order_id: r.seller_order_id, order_number: r.order_number })} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2" data-testid="ff-cards">
            {rows.map((r) => {
              const f = r.fulfillment || {}; const prog = f.progress || [];
              const scanned = prog.reduce((a, p) => a + (p.scanned_qty || 0), 0);
              const required = prog.reduce((a, p) => a + (p.required_qty || 0), 0);
              return (
                <div key={r.seller_order_id} className="bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-2 min-w-0">
                      {/* STEP OMS-OUTBOUND-UNIFY-2 · 모바일 카드 대표 이미지 */}
                      {(() => {
                        const first = (r.items || [])[0];
                        const img = first?.image_snapshot || first?.image || first?.thumbnail || '';
                        return (
                          <div className="flex-shrink-0 w-9 h-9 rounded overflow-hidden bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex items-center justify-center"
                               data-testid={`ff-thumb-mobile-${r.seller_order_id}`}>
                            {img
                              ? <img src={img} alt="" className="w-full h-full object-cover" loading="lazy"
                                     onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              : <Package size={13} className="text-gray-300" />}
                          </div>
                        );
                      })()}
                      <div className="min-w-0">
                        <p className="font-mono text-xs">{r.order_number}</p>
                        <p className="text-[11px] text-gray-500">{r.buyer_snapshot?.name || '-'} · {fmt(r.created_at)}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-black ${STATUS_CLS[f.status || 'pending']}`}>{STATUS_LABEL[f.status || 'pending']}</span>
                  </div>
                  <div className="text-[11px] mb-2">
                    {(r.items || []).slice(0, 2).map((it, i) => <div key={i}>{it.name_snapshot} × {it.quantity}</div>)}
                    {r.items?.length > 2 && <div className="text-[10px] text-gray-500">외 {r.items.length - 2}건</div>}
                    <div className="text-gray-500 mt-1">진행 <span className="font-black">{scanned}/{required}</span></div>
                  </div>
                  <RowActions row={r} onOpenScan={() => setScanTarget(r)} onPick={() => startPicking(r)} onRefresh={load} onOpenPartial={() => setPartialTarget({ seller_order_id: r.seller_order_id, order_number: r.order_number })} />
                </div>
              );
            })}
          </div>
        </>
      )}

      {scanTarget && (
        <ScanModal so={scanTarget} onClose={() => setScanTarget(null)} onDone={() => { setScanTarget(null); load(); }} />
      )}
      {labelIds && (
        <ShippingLabelModal open={!!labelIds} orderIds={labelIds} onClose={() => setLabelIds(null)} />
      )}
      {partialTarget && (
        <PartialShipmentModal open={!!partialTarget}
          sellerOrderId={partialTarget.seller_order_id}
          orderNumber={partialTarget.order_number}
          onClose={() => setPartialTarget(null)}
          onChanged={load} />
      )}
      {pickTarget && (
        <SelectivePickModal open={!!pickTarget}
          sellerOrderId={pickTarget.seller_order_id}
          orderNumber={pickTarget.order_number}
          onClose={() => setPickTarget(null)}
          onPicked={async (resp) => {
            // STEP OMS-PARTIAL-PICK-2A-FIX · pick 성공 → 서버 최신 fulfillment 로 스캐너 자동 오픈.
            // resp.fulfillment 는 선택한 item_refs 만 담긴 progress[] 를 갖는다.
            try {
              const r = await api.get(`/seller/fulfillment/order/${pickTarget.seller_order_id}`);
              setScanTarget(r.data);
            } catch { /* fall through - list refresh handles it */ }
            setPickTarget(null);
            load();
          }} />
      )}
    </div>
  );
}

// Export setter through props: pass down via context via closure — easier: forward to RowActions
FulfillmentSection.__usePartial = true;

function RowActions({ row, onOpenScan, onPick, onRefresh, onOpenPartial }) {
  const navigate = useNavigate();
  const f = row.fulfillment || {};
  const s = f.status || 'pending';
  const shipped = s === 'shipped';
  const hasTrack = !!(row.shipment && row.shipment.tracking_number);
  const [labelOpen, setLabelOpen] = useState(false);
  const doShip = async () => {
    if (!hasTrack) { toast.error('송장 미등록 — 등록 후 배송중 처리 가능합니다.'); return; }
    if (!window.confirm('배송중으로 변경할까요?')) return;
    try {
      await api.post(`/seller/fulfillment/${row.seller_order_id}/ship`);
      toast.success('배송중으로 처리되었습니다.');
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };
  return (
    <>
      <div className="flex flex-wrap gap-1">
        {s === 'pending' && <button onClick={onPick} className="px-2 py-1 rounded bg-[#FF3B30] text-white text-[11px] font-black" data-testid={`ff-pick-${row.seller_order_id}`}><Play size={10} className="inline mr-0.5"/>피킹시작</button>}
        {s === 'picking' && <button onClick={onOpenScan} className="px-2 py-1 rounded bg-amber-500 text-white text-[11px] font-black" data-testid={`ff-scan-${row.seller_order_id}`}><ScanLine size={10} className="inline mr-0.5"/>스캔·검수</button>}
        {/* D-6A-UX · 검수완료 + 송장미등록 → 혼동유발 disabled [출고 처리] 대신 ⑥ 송장 등록으로 이동 CTA 노출.
            송장 존재 시에는 기존 [출고 처리] semantics 그대로 유지. */}
        {s === 'inspected' && !hasTrack && (
          <button onClick={() => navigate(INVOICE_STAGE_ROUTE)}
                  className="px-2 py-1 rounded bg-blue-600 text-white text-[11px] font-black inline-flex items-center gap-0.5"
                  data-testid={`ff-goto-invoice-${row.seller_order_id}`}
                  title="⑥ 송장 등록 화면으로 이동해 송장번호를 입력하세요.">
            <ChevronRight size={10}/> ⑥ 송장 등록으로 이동
          </button>
        )}
        {s === 'inspected' && hasTrack && (
          <button onClick={doShip}
                  className="px-2 py-1 rounded bg-emerald-600 text-white text-[11px] font-black"
                  data-testid={`ff-ship-${row.seller_order_id}`}>
            <Truck size={10} className="inline mr-0.5"/>배송중 처리
          </button>
        )}
        {hasTrack && <button onClick={() => setLabelOpen(true)} className="px-2 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold" data-testid={`ff-print-${row.seller_order_id}`}>🖨 송장 출력</button>}
        <button onClick={onOpenPartial} className="px-2 py-1 rounded border border-indigo-300 dark:border-indigo-500/30 text-indigo-700 dark:text-indigo-300 text-[11px] font-black" data-testid={`ff-partial-${row.seller_order_id}`} title="합포·부분출고 관리">
          ⊞ 합포·부분출고
        </button>
        {!shipped && s !== 'pending' && <button onClick={onOpenScan} className="px-2 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold" data-testid={`ff-detail-${row.seller_order_id}`}>상세</button>}
      </div>
      {labelOpen && <ShippingLabelModal open={labelOpen} orderIds={[row.seller_order_id]} onClose={() => setLabelOpen(false)} />}
    </>
  );
}

function ScanModal({ so, onClose, onDone }) {
  const navigate = useNavigate();
  const [state, setState] = useState(so);
  const [busy, setBusy] = useState(false);
  const [refetching, setRefetching] = useState(true);
  const f = state.fulfillment || {};
  const s = f.status || 'pending';

  // STEP OMS-PARTIAL-PICK-2A-FIX · drawer 재진입 stale 방지.
  //   open 시마다 서버에서 최신 fulfillment/scan_history 를 재조회.
  //   React state cache 만으로 보여주지 않는다 (single source of truth = 서버).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/seller/fulfillment/order/${so.seller_order_id}`);
        if (alive) setState(r.data);
      } catch { /* keep prop state as fallback */ }
      finally { if (alive) setRefetching(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [so.seller_order_id]);

  // BARCODE/WMS A3 · Delegate raw server response back to seller UI.
  // Backend `/api/seller/fulfillment/{sid}/scan` is the single source of
  // truth for wrong-item / overscan / not-in-order judgments; we NEVER
  // recompute them frontend-side.
  const handleServerResult = (m) => {
    if (m?.error) {
      toast.error(m.error);
      return;
    }
    if (m?.fulfillment) {
      setState((prev) => ({ ...prev, fulfillment: m.fulfillment }));
    }
    if (m?.progress_entry) {
      const pe = m.progress_entry;
      toast.success(`${pe.product_name} · ${pe.scanned_qty}/${pe.required_qty}`);
    }
  };

  const revert = async () => {
    try {
      const r = await api.post(`/seller/fulfillment/${state.seller_order_id}/scan-revert`);
      setState({ ...state, fulfillment: r.data.fulfillment });
      toast.success('최근 스캔을 취소했습니다.');
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  // STEP OMS-PARTIAL-PICK-2A-FIX · 스캔 이력 개별 취소.  scan_id 로 서버가
  //   특정 event 를 안전하게 롤백 (중복취소·다른 상품 감소 방지는 서버에서 처리).
  const revertOne = async (scan_id) => {
    if (!scan_id) return;
    try {
      const r = await api.post(`/seller/fulfillment/${state.seller_order_id}/scan-revert-by-id`,
                                 { scan_id });
      setState({ ...state, fulfillment: r.data.fulfillment });
      toast.success('선택한 스캔을 취소했습니다.');
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  const complete = async () => {
    try {
      const r = await api.post(`/seller/fulfillment/${state.seller_order_id}/complete-inspection`);
      // STEP 3-SHIP-D-3R HOTFIX-2 · 부모 리스트 리페치 + 드로어 닫기 (ship() 과 동일 패턴).
      // 이전에는 setState만 하고 onDone 미호출 → 피킹중 목록에 stale row 잔류.
      setState({ ...state, fulfillment: r.data.fulfillment });
      const tn = state?.shipment?.tracking_number;
      toast.success(tn
        ? '검수 완료 · 이제 [배송중 처리] 로 배송중 전환 가능합니다.'
        : '검수 완료 · [주문] → ⑥ 송장 등록에 표시됩니다.');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  const ship = async () => {
    try {
      await api.post(`/seller/fulfillment/${state.seller_order_id}/ship`);
      toast.success('출고 처리되었습니다.');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  const progress = f.progress || [];
  const total = progress.reduce((a, p) => a + p.required_qty, 0);
  const scanned = progress.reduce((a, p) => a + p.scanned_qty, 0);
  const allDone = progress.length > 0 && progress.every((p) => p.scanned_qty >= p.required_qty);
  const hasTrack = !!(state.shipment && state.shipment.tracking_number);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ff-scan-drawer">
      <div className="flex-1 bg-black/50" onClick={onClose}/>
      <div className="w-full max-w-lg bg-white dark:bg-[#0A0A0A] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white dark:bg-[#0A0A0A] border-b border-black/10 dark:border-white/10 p-3 flex justify-between items-center">
          <div>
            <p className="text-sm font-black">상품 확인 · 바코드 스캔</p>
            <p className="text-[11px] text-gray-500 font-mono">{state.order_number}</p>
          </div>
          <button onClick={onClose} className="p-1"><X size={16}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className={`rounded-xl border p-2 text-center ${STATUS_CLS[s]}`}>
            <p className="text-[10px] font-black uppercase">{STATUS_LABEL[s]}</p>
            <p className="text-3xl font-black mt-1" data-testid="ff-scan-progress">{scanned}/{total}</p>
          </div>

          {s === 'picking' && (
            <>
              <div className="rounded-xl border-2 border-[#FF3B30] p-2 space-y-2">
                <p className="text-[10px] font-black uppercase text-[#FF3B30]">상품 바코드를 스캔하세요</p>
                {/* BARCODE/WMS A3 · REUSE shared scanner (USB/BT keyboard-wedge
                    + camera BarcodeDetector). scanUrl routes to seller server-
                    side endpoint; backend owns wrong-item/overscan judgment. */}
                <WarehouseScanInput
                  sid={state.seller_order_id}
                  scanUrl={`/seller/fulfillment/${state.seller_order_id}/scan`}
                  onScanResult={handleServerResult}
                  disabled={busy}
                />
                <button onClick={revert} className="w-full py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold" data-testid="ff-revert-btn"><Undo2 size={11} className="inline mr-0.5"/>최근 스캔 취소</button>
              </div>
              <button onClick={complete} disabled={!allDone} className="w-full py-3 rounded-full bg-blue-600 text-white text-xs font-black disabled:opacity-40" data-testid="ff-complete-btn"><CheckCircle2 size={11} className="inline mr-0.5"/>{allDone ? '✓ 상품 확인 완료' : '상품 확인 완료'}</button>
            </>
          )}

          {s === 'inspected' && (
            <>
              {/* D-6A-UX · 검수완료 + 송장미등록 → "송장 등록 필요" + ⑥ 송장 등록으로 이동 CTA.
                  기존 disabled [배송중 처리] 버튼은 hasTrack 시에만 노출 · semantics 무변경. */}
              {!hasTrack ? (
                <div className="rounded-lg bg-blue-50 border border-blue-300 dark:bg-blue-500/10 dark:border-blue-500/30 p-3 space-y-2" data-testid="ff-no-tracking">
                  <p className="text-xs font-black text-blue-900 dark:text-blue-200">검수 완료 · 송장 등록 필요</p>
                  <p className="text-[11px] text-blue-800 dark:text-blue-300">
                    이 주문은 <b>⑥ 송장 등록</b> 화면에 이미 표시됩니다. 아래 버튼으로 이동해 송장번호를 입력하세요.
                  </p>
                  <button onClick={() => navigate(INVOICE_STAGE_ROUTE)}
                          className="w-full py-2.5 rounded-full bg-blue-600 text-white text-xs font-black inline-flex items-center justify-center gap-1"
                          data-testid="ff-goto-invoice-btn">
                    <ChevronRight size={11}/> ⑥ 송장 등록으로 이동 →
                  </button>
                </div>
              ) : (
                <button onClick={ship} className="w-full py-3 rounded-full bg-emerald-600 text-white text-xs font-black" data-testid="ff-ship-btn"><Truck size={11} className="inline mr-0.5"/>배송중 처리</button>
              )}
            </>
          )}

          <div className="rounded-xl border border-black/10 dark:border-white/10 p-2" data-testid="ff-progress-list">
            <p className="text-[10px] font-black uppercase text-gray-500 mb-2">주문 상품 진행률</p>
            {progress.map((p, i) => {
              const done = p.scanned_qty >= p.required_qty;
              return (
                <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-black/5 dark:border-white/10 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{p.product_name}</p>
                    <p className="text-[10px] text-gray-500">{Object.entries(p.option || {}).map(([k, v]) => `${k}:${v}`).join(' · ') || '기본'}</p>
                  </div>
                  <p className={`font-mono font-black ${done ? 'text-emerald-600' : 'text-[#FF3B30]'}`} data-testid={`ff-prog-${p.product_id}-${p.variant_key}`}>
                    {p.scanned_qty} / {p.required_qty}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-black/10 dark:border-white/10 p-2 max-h-64 overflow-y-auto" data-testid="ff-scan-history">
            <p className="text-[10px] font-black uppercase text-gray-500 mb-1">스캔 이력 (개별 취소 가능)</p>
            {(f.scan_history || []).slice().reverse().slice(0, 20).map((h) => {
              // STEP OMS-PARTIAL-PICK-2A-FIX · 성공 scan (matched=true, action='scan', 미취소) 만
              //   개별 [취소] 버튼 노출.  revert 이벤트(scan_reverted) · 실패 스캔 · 이미 취소된
              //   이력은 안내만 표기.
              const canRevert = h.matched && h.action === 'scan' && !h.reverted && s === 'picking';
              return (
                <div key={h.id || `${h.at}-${h.code}`} className="text-[11px] py-1 flex items-center justify-between gap-2 border-b border-black/5 dark:border-white/10 last:border-b-0"
                     data-testid={`ff-scan-hist-${h.id || 'x'}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono truncate">{h.code || h.action}</div>
                    {h.product_name && <div className="text-[10px] text-gray-500 truncate">{h.product_name}</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className={`${h.reverted ? 'line-through text-gray-400' : (h.action === 'scan_reverted' ? 'text-gray-500' : (h.matched ? 'text-emerald-600' : 'text-red-600'))}`}>
                      {h.action === 'scan_reverted'
                        ? '↩ 되돌림'
                        : (h.matched ? (h.matched_by === 'barcode' ? '✓ BC' : '✓ SKU') : `× ${h.reason || '오류'}`)}
                    </span>
                    {canRevert && (
                      <button onClick={() => revertOne(h.id)}
                              className="px-1.5 py-0.5 rounded border border-black/10 dark:border-white/10 text-[10px] font-bold hover:border-[#FF3B30]/60 hover:text-[#FF3B30]"
                              data-testid={`ff-scan-hist-revert-${h.id}`}
                              title="이 스캔만 취소">
                        취소
                      </button>
                    )}
                    {h.reverted && <span className="text-[10px] text-gray-400" data-testid={`ff-scan-hist-reverted-${h.id}`}>취소됨</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
